import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef, useRef, useCallback, createElement } from "react";
import styled, { css } from "styled-components";
import { IconButton } from "@creditornot/cb-button";
import { colors, cbSemanticCssVars, typographyCss, semanticColors } from "@creditornot/cb-ingredients";
import { createCssVars, configureCssVars, createCssVarDefinitions, pxToRem, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
const AlertCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M11.984.003a12.205 12.205 0 00-8.51 3.652A11.799 11.799 0 00.002 12.21 11.778 11.778 0 0011.8 24h.214c6.677-.069 12.039-5.53 11.985-12.208A11.765 11.765 0 0011.984.003zM10.5 16.543a1.476 1.476 0 011.449-1.53h.027c.82.002 1.492.65 1.522 1.47a1.475 1.475 0 01-1.448 1.53h-.027a1.529 1.529 0 01-1.523-1.47zm.5-4.041v-6a1 1 0 012 0v6a1 1 0 01-2 0z" }) }));
const AlertCircleFilledIcon = AlertCircleFilled;
const CheckmarkCircleFilled = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M0 12C0 5.373 5.373 0 12 0c6.624.008 11.992 5.376 12 12 0 6.627-5.373 12-12 12S0 18.627 0 12zm17.552-5.78a1.25 1.25 0 011.197 1.977l-6.7 9.087a1.263 1.263 0 01-1.787.235l-4.783-3.826a1.251 1.251 0 011.563-1.953l3.767 3.014 5.924-8.04c.196-.267.49-.445.819-.495z"
    }
  ) })
);
const CheckmarkCircleFilledIcon = CheckmarkCircleFilled;
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const CrossIcon = SvgCross;
const CrossCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", fill: "none", ...props, ref, children: /* @__PURE__ */ jsx(
  "path",
  {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0 12C0 5.373 5.373 0 12 0c6.624.008 11.992 5.376 12 12 0 6.627-5.373 12-12 12S0 18.627 0 12zm16.782-6.034a1.249 1.249 0 01.884 2.134l-3.72 3.722a.25.25 0 000 .354l3.722 3.722a1.25 1.25 0 010 1.768 1.272 1.272 0 01-1.768 0l-3.724-3.72a.249.249 0 00-.353 0l-3.722 3.722a1.269 1.269 0 01-1.768 0 1.251 1.251 0 010-1.768l3.722-3.724a.25.25 0 000-.354L6.333 8.1A1.25 1.25 0 118.1 6.333l3.723 3.722a.25.25 0 00.353 0l3.722-3.722c.234-.235.552-.367.884-.367z"
  }
) }));
const CrossCircleFilledIcon = CrossCircleFilled;
const InfoCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm.25 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75c0 .138.112.25.25.25h.75a1 1 0 010 2z" }) }));
const InfoCircleFilledIcon = InfoCircleFilled;
const cssVars$1 = createCssVars([
  "height",
  "padding",
  "borderRadius",
  "borderWidth",
  "fontSize",
  "fontWeight",
  "iconSize",
  "clearButtonSize"
]);
const { cssString: colorCssVarDefinitions$1, cssVars: colorCssVars$1 } = configureCssVars({
  background: {
    light: colors.salt100,
    dark: "transparent"
  },
  backgroundDisabled: {
    light: colors.pepper8,
    dark: colors.salt8
  },
  borderActive: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  }
});
const ClearButton = styled(IconButton)`
  transition: all 150ms ease-in;
  transform: scale(0);
`;
const Root$2 = styled.div`
  ${({ $cssVarOverrides }) => createCssVarDefinitions({
  height: "36px",
  padding: "12px",
  borderRadius: "8px",
  borderWidth: "1px",
  fontSize: pxToRem(14),
  fontWeight: "400",
  iconSize: "16px",
  clearButtonSize: "24px",
  // default tiny IconButton size
  ...$cssVarOverrides
})}

  ${colorCssVarDefinitions$1}

  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  max-width: 100%;
  width: ${({ $stretch }) => $stretch ? "100%" : "auto"};
  min-height: ${cssVars$1.height};
  border-radius: ${cssVars$1.borderRadius};
  overflow: hidden;
  z-index: 0;
`;
const BG = styled.div`
  position: absolute;
  z-index: 0;
  box-sizing: border-box;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;

  border-radius: ${cssVars$1.borderRadius};
  background-color: ${colorCssVars$1.background};
  transition:
    background-color 75ms ease-in,
    border-color 75ms ease-in;
`;
const Border = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  z-index: 1;
  border-radius: ${cssVars$1.borderRadius};
  border: ${cssVars$1.borderWidth} solid ${cbSemanticCssVars.borderLight};

  pointer-events: none;
`;
const IconContainer$2 = styled.div`
  z-index: 1;
  position: absolute;
  height: 100%;
  width: 36px;
  display: flex;
  align-items: center;
  padding-inline-start: ${cssVars$1.padding};
`;
const SuffixText = styled.div`
  z-index: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typographyCss.Body3()}
  font-size: ${cssVars$1.fontSize};
  line-height: 1.43;
  text-align: end;
  padding-inline-end: ${cssVars$1.padding};
  color: ${cbSemanticCssVars.textSecondary};
`;
const ClearButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 0;
  opacity: 0;
  transition:
    opacity 120ms ease-in,
    width 300ms cubic-bezier(0.1, 0.8, 0.2, 1) /* easeOutCirc */;
  visibility: hidden;
  overflow: visible;
  width: calc(${cssVars$1.padding} + ${cssVars$1.clearButtonSize} / 2);
`;
const ActualInput = styled.input`
  background: transparent;
  outline: none;
  border: none;

  z-index: 1;
  position: relative;
  box-sizing: border-box;
  width: 100%;

  transition: color 100ms ease-in;

  flex: 1;
  display: flex;
  align-items: stretch;
  border-radius: ${cssVars$1.borderRadius};
  padding-top: 4px;
  padding-bottom: 4px;
  padding-inline-start: ${({ $leftPadding }) => $leftPadding === "withIconSpace" ? `calc(2 * ${cssVars$1.padding} + ${cssVars$1.iconSize})` : cssVars$1.padding};
  padding-inline-end: ${cssVars$1.padding};

  color: ${cbSemanticCssVars.textPrimary};
  ${typographyCss.Body3()}
  font-size: ${cssVars$1.fontSize};
  line-height: 1.43;
  font-weight: ${cssVars$1.fontWeight};

  ~ ${IconContainer$2} svg {
    width: var(--iconSize);
    fill: ${cbSemanticCssVars.textSecondary};
  }

  &::placeholder {
    color: ${cbSemanticCssVars.textSecondary};
  }

  &:focus {
    color: ${cbSemanticCssVars.textPrimary};

    ~ ${Border} {
      border-color: ${colorCssVars$1.borderActive};
    }

    ~ ${IconContainer$2} svg {
      fill: ${cbSemanticCssVars.textPrimary};
    }
  }

  &:disabled {
    color: ${cbSemanticCssVars.textSecondary};

    ~ ${IconContainer$2} svg {
      fill: ${cbSemanticCssVars.textSecondary};
    }
  }
  &:disabled ~ ${BG} {
    background-color: ${colorCssVars$1.backgroundDisabled};
  }
  &:disabled ~ ${SuffixText} {
    color: ${cbSemanticCssVars.textTertiary};
  }

  &[aria-invalid='true']:not(:focus) ~ ${Border} {
    border-color: ${colors.strawberry100};
  }

  &:not([type]),
  &[type='text'],
  &[type='tel'] {
    ${({ $fontStyle }) => $fontStyle === "numeric" ? `font-feature-settings: 'tnum', 'lnum', 'ss06';` : ""};
  }

  &[type='number']:not([step]) {
    /* Hide step arrows in  Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      /* stylelint-disable-next-line  property-no-vendor-prefix */
      -webkit-appearance: none;
      margin: 0;
    }

    /* Hide step arrows in Firefox */
    /* stylelint-disable-next-line  property-no-vendor-prefix */
    -moz-appearance: textfield;
  }

  &[type='number'] {
    font-feature-settings: 'tnum', 'lnum', 'ss06';
  }

  &[type='search'] {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      /* stylelint-disable-next-line  property-no-vendor-prefix */
      -webkit-appearance: none;
    }
  }

  /* Show clear button only when input has value */
  &:not([value='']) {
    & ~ ${ClearButtonContainer} {
      z-index: 2;
      opacity: 1;
      visibility: visible;
    }

    & ~ ${ClearButtonContainer} > ${ClearButton} {
      transform: scale(1);
    }
  }

  &[value=''] {
    & ~ ${ClearButtonContainer}[data-animate-width='true'] {
      width: 0;
    }
  }
`;
function useMergedRef(...refs) {
  return useCallback(
    (element) => {
      for (const ref of refs) {
        if (typeof ref === "function") {
          ref(element);
        } else if (ref && typeof ref === "object") {
          ref.current = element;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs
  );
}
const disableValueChangeOnWheel = (event, callback) => {
  const target = event.currentTarget;
  target.blur();
  setTimeout(() => {
    target.focus({ preventScroll: true });
  }, 0);
  callback == null ? void 0 : callback(event);
};
const Input = React.forwardRef(
  ({
    autoComplete = "off",
    className,
    clearButtonAriaLabel = "Clear",
    cssVarOverrides,
    disabled = false,
    enableValueChangeOnScrollWheel,
    fontStyle = "normal",
    icon,
    iconTitle,
    inputRef,
    invalid = false,
    onChange,
    onClearClick,
    rootRef,
    spellCheck = false,
    stretch = true,
    style,
    suffixText,
    value,
    ...rest
  }, ref) => {
    const innerRef = useRef(null);
    const mergedRef = useMergedRef(inputRef ?? ref, innerRef);
    const onMouseDown = (event) => {
      var _a;
      event.preventDefault();
      (_a = innerRef.current) == null ? void 0 : _a.focus();
    };
    return /* @__PURE__ */ jsxs(
      Root$2,
      {
        $cssVarOverrides: cssVarOverrides,
        $stretch: stretch,
        className,
        ref: rootRef,
        style,
        children: [
          /* @__PURE__ */ jsx(
            ActualInput,
            {
              ...rest,
              $fontStyle: fontStyle,
              $leftPadding: icon ? "withIconSpace" : "default",
              "aria-invalid": invalid,
              autoComplete,
              disabled,
              onChange,
              onWheel: rest.type === "number" && !enableValueChangeOnScrollWheel ? (e) => disableValueChangeOnWheel(e, rest.onWheel) : rest.onWheel,
              ref: mergedRef,
              spellCheck,
              value
            }
          ),
          /* @__PURE__ */ jsx(BG, {}),
          /* @__PURE__ */ jsx(Border, {}),
          !!icon && /* @__PURE__ */ jsx(IconContainer$2, { "aria-hidden": true, onMouseDown, title: iconTitle, children: icon }),
          !!suffixText && /* @__PURE__ */ jsx(SuffixText, { onMouseDown, role: "presentation", children: suffixText }),
          !!onClearClick && /* @__PURE__ */ jsx(ClearButtonContainer, { "data-animate-width": !!suffixText, children: /* @__PURE__ */ jsx(
            ClearButton,
            {
              alignment: "iconEdges",
              "aria-label": clearButtonAriaLabel,
              background: "animated",
              cssVarOverrides: (cssVarOverrides == null ? void 0 : cssVarOverrides.clearButtonSize) ? { size: cssVarOverrides == null ? void 0 : cssVarOverrides.clearButtonSize } : void 0,
              disabled,
              onClick: onClearClick,
              onMouseDown,
              size: "tiny",
              tabIndex: value === void 0 || value === null || value === "" ? -1 : 0,
              variant: "grey",
              children: /* @__PURE__ */ jsx(CrossIcon, {})
            }
          ) })
        ]
      }
    );
  }
);
Input.displayName = "Input";
const cssVars = createCssVars(["fontSize", "iconSize", "lineHeight"]);
const Root$1 = styled.div`
  ${({ $cssVarOverrides }) => createCssVarDefinitions({
  fontSize: pxToRem(12),
  iconSize: "var(--fontSize)",
  lineHeight: `calc(var(--fontSize) * ${16 / 12})`,
  ...$cssVarOverrides
})}
  display: flex;
`;
const Col = styled.div`
  display: flex;
  flex-direction: column;
`;
const LeftCol = styled(Col)`
  flex-shrink: 0;
  padding-inline-end: calc(2 / 3 * ${cssVars.fontSize});
`;
const RightCol = styled(Col)`
  flex: 1;
  overflow: hidden;
`;
const IconContainer$1 = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${cssVars.lineHeight};
`;
const Message = styled.div`
  max-width: 100%;
  text-overflow: ellipsis;
  ${typographyCss.Small()}
  font-size: ${cssVars.fontSize};
  line-height: ${cssVars.lineHeight};
`;
const variantConfigs = {
  error: {
    icon: styled(CrossCircleFilledIcon)`
      display: block;
      width: ${cssVars.iconSize};
      fill: ${colors.strawberry100};
    `
  },
  info: {
    icon: styled(InfoCircleFilledIcon)`
      ${createThemedCssVarDefinitions({
      fill: {
        light: colors.space100,
        dark: colors.salt100
      }
    })}
      display: block;
      width: ${cssVars.iconSize};
      fill: var(--fill);
    `
  },
  success: {
    icon: styled(CheckmarkCircleFilledIcon)`
      display: block;
      width: ${cssVars.iconSize};
      fill: ${colors.lime100};
    `
  },
  warning: {
    icon: styled(AlertCircleFilledIcon)`
      display: block;
      width: ${cssVars.iconSize};
      fill: ${colors.banana100};
    `
  }
};
const InputAlert = ({ children, className, cssVarOverrides, style, variant = "error" }) => /* @__PURE__ */ jsxs(Root$1, { $cssVarOverrides: cssVarOverrides, className, style, children: [
  /* @__PURE__ */ jsx(LeftCol, { children: /* @__PURE__ */ jsx(IconContainer$1, { children: createElement(variantConfigs[variant].icon) }) }),
  /* @__PURE__ */ jsx(RightCol, { children: /* @__PURE__ */ jsx(Message, { children }) })
] });
const PADDING_TOP = 7;
const PADDING_BOTTOM = 8;
const LINE_HEIGHT = 20;
const BORDER_WIDTH = 1;
const VERTICAL_PADDING_AND_BORDER = 2 * BORDER_WIDTH + PADDING_TOP + PADDING_BOTTOM;
const IconContainer = styled.div`
  position: absolute;
  top: 10px;
  margin-inline-start: 8px;
  height: 16px;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;
const { cssString: colorCssVarDefinitions, cssVars: colorCssVars } = configureCssVars({
  background: {
    light: colors.salt100,
    dark: "transparent"
  },
  backgroundDisabled: {
    light: colors.pepper8,
    dark: colors.salt8
  },
  borderActive: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  scrollbarHover: {
    light: colors.pepper32,
    dark: colors.salt36
  },
  scrollbarThumb: {
    light: semanticColors.light.surfaceMain,
    dark: semanticColors.dark.surfaceMain
  },
  scrollbarThumbDisabled: {
    light: "#EEEEEE",
    dark: "#262626"
  }
});
const textareaBase = css`
  ${colorCssVarDefinitions}

  display: inline-block;
  grid-area: 1 / 1;
  outline: none;
  position: relative;
  box-sizing: border-box;
  margin: 0; /* Remove margin that Firefox and Edge add by default */
  min-height: 36px;
  /* overflow-y:scroll is required for autosizing to work correctly. Without it, a scrollbar would
  sometimes unnecessarily appear to the <textarea>, but not to the sizer element. This caused  the
  line-wrapping to get out of sync between the <textarea> and the sizer element. */
  overflow-y: scroll;

  border-radius: 8px;
  border: ${BORDER_WIDTH}px solid ${cbSemanticCssVars.borderLight};
  background-color: ${colorCssVars.background};
  padding-top: ${PADDING_TOP}px;
  padding-bottom: ${PADDING_BOTTOM}px;
  padding-inline-start: ${({ $hasIcon }) => $hasIcon ? 39 : 11}px;
  padding-inline-end: 8px;

  transition:
    color 75ms ease-in,
    background-color 75ms ease-in,
    border-color 75ms ease-in;

  color: ${cbSemanticCssVars.textPrimary};
  ${typographyCss.Body3()}
  font-weight: 400;

  ~ ${IconContainer} svg {
    width: 16px;
    fill: ${cbSemanticCssVars.textSecondary};
  }

  &::placeholder {
    color: ${cbSemanticCssVars.textSecondary};
  }

  &:focus {
    color: ${cbSemanticCssVars.textPrimary};

    border-color: ${colorCssVars.borderActive};

    ~ ${IconContainer} svg {
      color: ${cbSemanticCssVars.textPrimary};
    }
  }

  &:disabled {
    background: ${colorCssVars.backgroundDisabled};
    color: ${cbSemanticCssVars.textSecondary};

    ~ ${IconContainer} svg {
      fill: ${cbSemanticCssVars.textSecondary};
    }
  }

  &:not(:focus):invalid,
  &:not(:focus)[aria-invalid='true'] {
    border-color: ${colors.strawberry100};
  }

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0) transparent;

  &:hover {
    scrollbar-color: ${colorCssVars.scrollbarHover} transparent;
  }

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    border: none;
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 8px;
    overflow: hidden;
    background: transparent;
  }
  &::-webkit-scrollbar-track-piece {
    border: none;
  }
  &::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 20px;
    border: 3px solid ${colorCssVars.scrollbarThumb};
    transition: border-color 75ms ease-in;
  }
  &:disabled::-webkit-scrollbar-thumb {
    border-color: ${colorCssVars.scrollbarThumbDisabled};
  }
  &:hover::-webkit-scrollbar-thumb {
    background-color: ${colorCssVars.scrollbarHover};
  }
`;
const Root = styled.div`
  position: relative;
  z-index: 0;

  box-sizing: border-box;
  display: inline-grid;
  max-width: 100%;
  width: ${({ $stretch }) => $stretch ? "100%" : "auto"};
  border-radius: 8px;
  overflow: hidden;
`;
const Sizer = styled.div`
  ${textareaBase}
  max-height: ${({ $maxHeight }) => $maxHeight === Infinity ? "none" : `${$maxHeight}px`};
  visibility: hidden;
  white-space: pre-wrap;
`;
const ActualTextarea = styled.textarea`
  ${textareaBase}
  resize: ${({ $disableHorizontalResize, $disableVerticalResize }) => {
  if ($disableHorizontalResize && $disableVerticalResize) {
    return "none";
  }
  if ($disableHorizontalResize && !$disableVerticalResize) {
    return "vertical";
  }
  if (!$disableHorizontalResize && $disableVerticalResize) {
    return "horizontal";
  }
  if (!$disableHorizontalResize && !$disableVerticalResize) {
    return "both";
  }
  return "auto";
}};
`;
const Textarea = React.forwardRef(
  ({
    autoComplete = "off",
    autoresizeMaxRows,
    className,
    defaultValue,
    disableHorizontalResize = false,
    disableVerticalResize = false,
    icon,
    iconTitle,
    inputRef,
    invalid,
    rootRef,
    rows,
    spellCheck = false,
    stretch = true,
    style,
    value,
    ...rest
  }, ref) => {
    const shouldStretch = autoresizeMaxRows !== void 0 || stretch;
    return /* @__PURE__ */ jsxs(Root, { $stretch: shouldStretch, className, ref: rootRef, style, children: [
      /* @__PURE__ */ jsx(
        ActualTextarea,
        {
          ...rest,
          $disableHorizontalResize: shouldStretch || disableHorizontalResize,
          $disableVerticalResize: autoresizeMaxRows !== void 0 || disableVerticalResize,
          $hasIcon: !!icon,
          "aria-invalid": invalid,
          autoComplete,
          defaultValue,
          ref: ref ?? inputRef,
          rows: rows ?? (autoresizeMaxRows === void 0 ? void 0 : 1),
          spellCheck,
          value
        }
      ),
      !!icon && /* @__PURE__ */ jsx(IconContainer, { title: iconTitle, children: icon }),
      autoresizeMaxRows !== void 0 && /* @__PURE__ */ jsxs(
        Sizer,
        {
          $hasIcon: !!icon,
          $maxHeight: VERTICAL_PADDING_AND_BORDER + LINE_HEIGHT * autoresizeMaxRows,
          role: "presentation",
          children: [
            value,
            /* Trailing space is required, because without it an empty line at the end does not expand the sizer */
            " "
          ]
        }
      )
    ] });
  }
);
Textarea.displayName = "Textarea";
export {
  Input,
  InputAlert,
  Textarea
};
//# sourceMappingURL=index.js.map
